import React, { useContext, useMemo, useState } from 'react';
import Clipboard from 'react-clipboard.js';
import cx from 'classnames';
import FlashContext from '~/App/Components/Flash/Context';
import ActionLinks, { ActionLink } from './ActionLinks';
import { UserWebAsset } from '~/types/models';
import { DeleteSocialModal } from '../../Socials/Components/Modals/DeleteSocialModal';
import {
  useDeleteUserWebAsset,
  userWebAssetsQueryKeys,
} from '../Api/UserWebAsset';
import { useQueryClient } from 'react-query';

export interface WebAssetActionLinksProps {
  asset: UserWebAsset;
}

export default function WebAssetActionLinks({
  asset,
}: WebAssetActionLinksProps) {
  const { createFlash } = useContext(FlashContext);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: deleteUserAsset, isLoading: isDeleting } =
    useDeleteUserWebAsset({
      onSuccess: () => {
        void queryClient.invalidateQueries(userWebAssetsQueryKeys.lists());
        setShowDeleteConfirmationModal(false);
        createFlash({
          id: 'socials-deleted-flash',
          title: 'Deleted',
          message: 'Socials deleted',
        });
      },
      onError: () => {
        setShowDeleteConfirmationModal(false);
        createFlash({
          id: 'socials-deleted-flash',
          title: 'Error',
          message: 'Failed to delete the social',
        });
      },
    });

  const closeDeleteConfirmationModal = () =>
    setShowDeleteConfirmationModal(false);

  const handleOnTextCopy = () => {
    createFlash({
      id: 'copy-text-success',
      title: 'Success',
      message: 'Copied to clipboard',
    });
  };

  const onSocialDelete = () => void deleteUserAsset({ uuid: asset.uuid });

  const defaultLinkClass = cx('hover:!v2-bg-yellow-100');

  const deleteButtonClass = cx('v2-text-orange-700');

  const deleteButtonContainerClass = cx('hover:!v2-bg-magenta-50');

  const links = useMemo<ActionLink[]>(() => {
    return [
      {
        label: (
          <Clipboard
            component='div'
            data-clipboard-text={asset.url}
            onSuccess={handleOnTextCopy}
            className='flex items-center gap-x-4'
          >
            <i className='fa fa-copy' />{' '}
            <span className='text-sm'>Copy original link</span>
          </Clipboard>
        ),
        className: defaultLinkClass,
      },
      {
        label: (
          <>
            <i className={`fa fa-trash ${deleteButtonClass}`} />{' '}
            <span className={`text-sm  ${deleteButtonClass}`}>Delete</span>
          </>
        ),
        onClick: () => setShowDeleteConfirmationModal(true),
        className: deleteButtonContainerClass,
      },
    ] as ActionLink[];
  }, [asset]);

  return (
    <>
      <ActionLinks links={links} />
      <DeleteSocialModal
        open={showDeleteConfirmationModal}
        onConfirm={onSocialDelete}
        onCancel={closeDeleteConfirmationModal}
        deleting={isDeleting}
      />
    </>
  );
}
